// StockMobile.jsx — « Gestion du Stock » en version mobile.
// Coquille unique : un Segmented remplace les Tabs AntD du desktop, chaque onglet
// rend son propre écran en cartes. Mêmes permissions et mêmes deep-links que
// GestionStock.jsx (?tab=…, ?product_id=…&create=1).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ProductScreen, Appbar, Segmented, EmptyState, useMobileMenu } from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { STOCK_HISTORY_PERMISSIONS } from "../../gestion-stock/GestionStock";
import { PERM } from "./stockMobileShared";
import StockTransfersMobile from "./StockTransfersMobile";
import StockAdjustmentsMobile from "./StockAdjustmentsMobile";
import StockHistoryMobile from "./StockHistoryMobile";
import StockReplenishmentReturnModal from "../../components/StockReplenishmentReturnModal";
import { isReturnableStockReplenishment } from "../../utils/stockReplenishment";
import { discardSalesDraftFromReplenishment } from "../../utils/salesReplenishmentDraft";

const TAB_KEYS = ["transfers", "adjustments", "history"];

export default function StockMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();

  const params = new URLSearchParams(location.search);
  const requestedTab = params.get("tab");
  const requestedProductId = Number(params.get("product_id") || 0) || null;
  const replenishmentPrefill = location.state?.stockReplenishment || null;
  const autoOpenCreate =
    params.get("create") === "1" || Boolean(replenishmentPrefill);
  const [completedReplenishment, setCompletedReplenishment] = useState(null);
  const consumeReplenishmentPrefill = useCallback(() => {
    if (!replenishmentPrefill) return;
    discardSalesDraftFromReplenishment(replenishmentPrefill);
    const nextState = { ...(location.state || {}) };
    delete nextState.stockReplenishment;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    replenishmentPrefill,
  ]);

  const canViewHistory = STOCK_HISTORY_PERMISSIONS.some((p) => hasPermission(p));

  const handleReplenishmentCancelled = useCallback(() => {
    setCompletedReplenishment(null);
    consumeReplenishmentPrefill();
  }, [consumeReplenishmentPrefill]);

  const handleReplenishmentCompleted = useCallback(() => {
    if (isReturnableStockReplenishment(replenishmentPrefill)) {
      setCompletedReplenishment(replenishmentPrefill);
      return;
    }

    consumeReplenishmentPrefill();
  }, [consumeReplenishmentPrefill, replenishmentPrefill]);

  const handleReturnAfterReplenishment = useCallback(
    (returnNavigation) => {
      setCompletedReplenishment(null);
      navigate(returnNavigation.path, {
        replace: true,
        state: returnNavigation.state,
      });
    },
    [navigate]
  );

  const tabs = useMemo(() => {
    const out = [];
    if (hasPermission(PERM.transferView)) out.push({ value: "transfers", label: "Transferts" });
    if (hasPermission(PERM.adjustView)) out.push({ value: "adjustments", label: "Ajustements" });
    if (canViewHistory) out.push({ value: "history", label: "Historique" });
    return out;
  }, [hasPermission, canViewHistory]);

  const [tab, setTab] = useState(tabs[0]?.value || "history");

  useEffect(() => {
    if (requestedTab && TAB_KEYS.includes(requestedTab)) setTab(requestedTab);
  }, [requestedTab]);

  // Sans magasin, seul l'historique a du sens (règle du desktop, GestionStock.jsx:83-84).
  const active = !selectedWarehouse && canViewHistory ? "history" : tab;
  const visibleTab = tabs.some((t) => t.value === active) ? active : tabs[0]?.value;

  if (!tabs.length) {
    return (
      <ProductScreen>
        <Appbar title="Gestion du Stock" onMenu={onMenu} />
        <div className="body">
          <EmptyState
            icon="ban" title="Accès restreint"
            text="Vous n'avez pas la permission de voir les transferts, les ajustements ou l'historique."
          />
        </div>
      </ProductScreen>
    );
  }

  return (
    <ProductScreen>
      <Appbar
        title="Gestion du Stock"
        onMenu={onMenu}
        subtitle={selectedWarehouse ? "Transferts, ajustements et historique" : "Historique global de l'entreprise"}
      />

      {/* paddingTop:0 annule le safe-area que .appbar réapplique à chaque instance ;
          borderBottom:0 pour que l'en-tête, les onglets et les filtres forment un bloc. */}
      {tabs.length > 1 ? (
        <div className="appbar" style={{ paddingTop: 0, paddingBottom: 0, borderBottom: 0 }}>
          <Segmented value={visibleTab} onChange={setTab} options={tabs} />
        </div>
      ) : null}

      {visibleTab === "transfers" ? (
        <StockTransfersMobile
          selectedWarehouse={selectedWarehouse}
          hasPermission={hasPermission}
        />
      ) : null}

      {visibleTab === "adjustments" ? (
        <StockAdjustmentsMobile
          selectedWarehouse={selectedWarehouse}
          selectedCompany={selectedCompany}
          hasPermission={hasPermission}
          user={user}
          initialProductId={requestedProductId}
          autoOpenCreate={autoOpenCreate}
          initialItems={replenishmentPrefill?.items || []}
          prefillRequestId={replenishmentPrefill?.request_id || null}
          replenishmentContext={replenishmentPrefill}
          onReplenishmentCompleted={handleReplenishmentCompleted}
          onReplenishmentCancelled={handleReplenishmentCancelled}
        />
      ) : null}

      {visibleTab === "history" ? (
        <StockHistoryMobile
          selectedWarehouse={selectedWarehouse}
          selectedCompany={selectedCompany}
        />
      ) : null}

      <StockReplenishmentReturnModal
        open={Boolean(completedReplenishment)}
        replenishment={completedReplenishment}
        method="adjustment"
        onStay={handleReplenishmentCancelled}
        onReturn={handleReturnAfterReplenishment}
      />
    </ProductScreen>
  );
}
